import { ReactNode, useEffect } from "react";
import { DictionaryLink, Language } from "../../../rest/models";
import { getModelArr } from "../../../rest/slice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { groupBy } from "../../../utils";
import { PleaseWait, DictRequestStatusDisplay } from "../../CommonComponents";
import { Anchor } from "../../RequestRespectingInputs";
import { RenderBlock } from "./RenderBlock";
import { bookReaderActions } from "./slice";
import { useLocation, useNavigate } from "react-router-dom";
import { RenderPlainTextParagraph } from "./RenderPlainTextParagraph";
import { EWRSaveButton } from "./editwhilereading/EWRSaveButton";

export function BookReadComponent(props: Props)
{
    const appDispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);

    //If this URL parameter is defined and set to 1, jump to the bookmarked sentence.
    //Also, the book opens on the page that contains that sentence.
    const goToBookmark = searchParams.get('goToBookmark') === '1';
    const page = parseInt(searchParams.get('page') || '0');

    const dictModels = getModelArr(useAppSelector, DictionaryLink);
    const langModels = getModelArr(useAppSelector, Language);

    const state = useAppSelector(function(state)
    {
        const sbr = state.bookReader;

        /*
            Get the bookmark from the URL by default.
            Helps with that weird-ass behavior on my phone where it spontaneously rerenders the page,
            but apparently doesn't get the latest data. I don't know how it works, but the URL is the
            most reliable way to keep data between such events.

            If the URL doesn't have the bookmark, fall back to the state.
        */

        const bm0 = searchParams.get('bookmark');

        //We add 1 to the param so it starts at 1, like the sentences are numbered on the page.
        //But in the DB, sentences are numbered from 0.
        const bm1 = parseInt(bm0 || '0') - 1;
        const bm2 = sbr.book?.bookmark;
        
        const ret =
        {
            bookId: sbr.book.id,
            bookmark: bm0 != null ? bm1 : bm2,
            
            isHtml: sbr.book.is_html,
            blocksByParent: sbr.blocksByParent,
            blocksOnPage: sbr.pagination.blocks,
            pages: sbr.pagination,
            pageCurrent: sbr.pagination.current,
            pageSize: sbr.book.page_size,
            unsavedChanges: sbr.book.unsaved_changes,
        }

        return ret;
    });
    
    const {bookmark, blocksByParent, pageSize, pages, pageCurrent, unsavedChanges} = state;
    const hasData = state.bookId && dictModels != null && langModels != null;

    useEffect(function()
    {
        searchParams.set('bookmark', (bookmark + 1) + '');
        const to = 
        {
            pathname: location.pathname,
            search: searchParams + ''
        }
        navigate(to, {replace: true, state: 'replace'});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookmark]);

    const elmUnsavedChanges = unsavedChanges
        ? <div className='ernots_BookUnsavedChanges'>There are unsaved changes. <EWRSaveButton/></div>
        : <></>;

    function getPageUrl(page: number)
    {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('page', page + '');
        return {pathname: location.pathname, search: searchParams.toString()}
    }

    function getPageLink(page: number, text: string, active: boolean, key?: string|number)
    {
        if (active)
        {
            return <Anchor key={key} to={getPageUrl(page)} replace>{text}</Anchor>;
        }
        else
        {
            return <b key={key}>{text}</b>
        }
    }

    let ret = <>
        <PleaseWait what='book data'/>
        <DictRequestStatusDisplay modelClass={Language} />
        <DictRequestStatusDisplay modelClass={DictionaryLink} />
    </>;

    useEffect(function()
    {
        if (!hasData)
        {
            return;
        }

        /*
            Do this the very first time, when data becomes available, and also whenever the page number changes in the URL.
            If page is passed in the URL, go to it.
            If goToBookmark, do that, but page takes priority.
            If neither is there, load page 1.

            setCurrentPage not only sets the page, but also recomputes what should be in it.
        */

        let page2 = 1;

        if (page)
        {
            page2 = page;
        }
        else if (pageSize && goToBookmark)
        {
            page2 = Math.floor(bookmark / pageSize) + 1;
        }

        appDispatch(bookReaderActions.setCurrentPage(page2));
    }, [appDispatch, hasData, bookmark, goToBookmark, page, pageSize]);

    useEffect(function()
    {
        if (!hasData)
        {
            return;
        }
        
        //Whenever the page number changes, start by scrolling to the top.
        window.scrollTo(0, 0)

        if (!goToBookmark)
        {
            return;
        }

        window.setTimeout(function()
        {
            const bookmarkedSentence = document.querySelector('.ernots_RenderSentence[data-bookmarked=true]');
            const anySentence = document.querySelector('.ernots_RenderSentence');

            if (bookmarkedSentence) 
            {
                //Go to the bookmark if it is on the current page.
                bookmarkedSentence.scrollIntoView();
            }
            else if (anySentence)
            {
                //Scroll to the first sentence on the page.
                anySentence.scrollIntoView();
            }
        }, 1);
    }, [goToBookmark, hasData, pageCurrent]);
    
    if (hasData)
    {
        const dictDict = groupBy(dictModels, dl => dl.language || '', dl => dl.text);

        const rootBlocks = blocksByParent[-1] ?? [];
        const blocks = rootBlocks
            .filter(b => state.blocksOnPage[b.block_id])
            .map(function(b)
            {
                const props2 =
                {
                    block: b,
                    dict_links: dictDict,
                    isPreview: props.isPreview
                };
                
                return (state.isHtml)
                    ? <RenderBlock key={b.key} {...props2}/>
                    : <RenderPlainTextParagraph key={b.key} {...props2}/>;
            });

        const pageLinks = [] as ReactNode[];
        for (let i = 1; i <= pages.total; i++)
        {
            pageLinks.push(getPageLink(i, i.toString(), i !== pages.current, i));
            pageLinks.push(' ');
        }
        
        const navigation = (
            <div>
                Pages: 
                {getPageLink(pages.current - 1, 'Prev', pages.current > 1)}
                {' '}
                {pageLinks}
                {getPageLink(pages.current + 1, 'Next', pages.current < pages.total)}
            </div>
        );

        ret = (
        <div className='ernots_BookReadComponent'>
            {elmUnsavedChanges}
            {navigation}
            {blocks}
            {navigation}
        </div>);
    }

    return ret;
}

interface Props
{
    isPreview: boolean;
}